import { useState } from "react"
import { Link, router } from "@inertiajs/react"
import { Pencil } from "lucide-react"
import { StepShell } from "../components/StepShell"
import { LifeWheelRadar } from "@/components/planning/LifeWheelRadar"
import { ProspectAnswersList } from "@/components/planning/ProspectAnswersList"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { AUDIT_CATEGORIES } from "@/components/planning/audit-questions"

// Review is the final intake step, but it's also the page members land
// on when they "open the full assessment" from the dashboard — so the
// cards below are read-only summaries with an explicit Edit affordance
// per section. Click Edit, land on that section's step, change what you
// need, hit Continue, and you come back here. The backend PATCH works
// regardless of report status (draft vs. submitted vs. generated).
export function Review({ data, report, wizard }) {
  const plan = data.annualPlan
  const quarter = data.quarter
  const isPeerGroup = wizard.mode.key === "peer_group"
  const isGoalsOnly = wizard.mode.key === "goals_only"
  const [processing, setProcessing] = useState(false)

  const submit = () => {
    setProcessing(true)
    router.post(
      "/app/plan/intake/finalize",
      {},
      { onFinish: () => setProcessing(false) },
    )
  }

  const alreadySubmitted = report.status !== "draft"
  const submitLabel = isGoalsOnly ? "Save my plan" : "Submit my assessment"
  const submittedLabel = isGoalsOnly ? "Already saved" : "Already submitted"

  return (
    <StepShell
      title={isGoalsOnly ? "Your plan" : "Your assessment"}
      subtitle={
        alreadySubmitted
          ? `${submittedLabel} — but you can still refine any section. Hit Edit on a card to update it.`
          : isGoalsOnly
            ? "Here's everything you've captured. Hit Edit on any section to change it, or save to put your plan live on your dashboard."
            : "Here's everything you've captured. Hit Edit on any section to change it, or submit to generate your personalized report."
      }
      onNext={submit}
      nextLabel={alreadySubmitted ? submittedLabel : submitLabel}
      nextDisabled={alreadySubmitted}
      processing={processing}
    >
      <div className="space-y-6">
        {isPeerGroup ? (
          <PeerGroupReviewCards quarter={quarter} report={report} />
        ) : (
        <>
        {/* A mode with no wheel has no wheel data and no audit step to
            edit-link into — rendering these would show a zeroed radar with
            dead Edit links. */}
        {wizard.mode.collectsWheel && (
          <>
            <EditableCard title="Your Integrated Life Audit" editStep="audit">
              <LifeWheelRadar scores={quarter.wheelTotals || {}} size={260} />
            </EditableCard>

            <EditableCard title="Category scores" editStep="audit">
              <ul className="space-y-1 text-sm">
                {AUDIT_CATEGORIES.map(({ key, label }) => (
                  <li
                    key={key}
                    className="flex justify-between border-b border-zinc-100 py-1 last:border-0"
                  >
                    <span>{label}</span>
                    <span className="font-medium">
                      {quarter.wheelTotals?.[key] ?? 0}/25
                    </span>
                  </li>
                ))}
              </ul>
            </EditableCard>
          </>
        )}

        <EditableCard title="One-year plan" editStep="one_year_plan">
          <div className="text-sm space-y-2">
            <p>
              <strong>Revenue target:</strong>{" "}
              {plan.revenueTarget
                ? `$${Number(plan.revenueTarget).toLocaleString()}`
                : "—"}
            </p>
            <p>
              <strong>Profit target:</strong>{" "}
              {plan.profitTarget
                ? `$${Number(plan.profitTarget).toLocaleString()}`
                : "—"}
            </p>
            <p>
              <strong>Target date:</strong> {plan.targetDate || "—"}
            </p>
            {plan.measurables && (
              <p>
                <strong>Measurables:</strong> {plan.measurables}
              </p>
            )}
            {plan.issues?.length > 0 && (
              <div>
                <strong>Issues:</strong>
                <ul className="list-disc pl-5 mt-1 space-y-0.5">
                  {plan.issues
                    .filter((i) => i.text)
                    .map((i, idx) => (
                      <li key={i.id || idx}>{i.text}</li>
                    ))}
                </ul>
              </div>
            )}
          </div>
        </EditableCard>

        <EditableCard title="Top goals" editStep="personal_goals">
          <ListSection
            items={plan.personalGoals}
            field="text"
            empty="No goals yet."
          />
        </EditableCard>

        <EditableCard title="KPIs" editStep="kpis">
          <KpiList items={plan.annualKpis} />
        </EditableCard>

        <EditableCard title="KOIs" editStep="kois">
          <KpiList items={plan.annualKois} />
        </EditableCard>

        <EditableCard title="Ministry in business" editStep="ministry">
          <ListSection
            items={plan.ministryGoals}
            field="text"
            empty="None yet."
          />
        </EditableCard>

        <EditableCard title="This quarter's rocks" editStep="quarter_rocks">
          <ListSection
            items={quarter.rocks}
            field="title"
            empty="No rocks yet."
          />
        </EditableCard>
        </>
        )}
      </div>
    </StepShell>
  )
}

// Peer-group prospects review just two things: their Integrated Life Audit and the
// 10 answers — not the planning cards. Each is editable back into its step.
function PeerGroupReviewCards({ quarter, report }) {
  return (
    <>
      <EditableCard title="Your Integrated Life Audit" editStep="audit">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 items-start">
          <LifeWheelRadar scores={quarter.wheelTotals || {}} size={240} />
          <ul className="space-y-1 text-sm">
            {AUDIT_CATEGORIES.map(({ key, label }) => (
              <li
                key={key}
                className="flex justify-between border-b border-zinc-100 py-1 last:border-0"
              >
                <span>{label}</span>
                <span className="font-medium">
                  {quarter.wheelTotals?.[key] ?? 0}/25
                </span>
              </li>
            ))}
          </ul>
        </div>
      </EditableCard>

      <EditableCard title="Your answers" editStep="peer_questions">
        <ProspectAnswersList answers={report?.prospectAnswers} />
      </EditableCard>
    </>
  )
}

// Read-only card with an explicit Edit link in the header that jumps
// straight into the corresponding intake step. Members use this as
// their primary edit surface — no need to Back through the wizard.
function EditableCard({ title, editStep, children }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <CardTitle>{title}</CardTitle>
        <Link
          href={`/app/plan/intake?step=${editStep}`}
          className="inline-flex items-center gap-1 text-xs text-kf-red hover:underline shrink-0"
          aria-label={`Edit ${title}`}
        >
          <Pencil className="w-3 h-3" />
          Edit
        </Link>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  )
}

function ListSection({ items = [], field, empty }) {
  if (!items.length)
    return <p className="text-zinc-500 italic text-sm">{empty}</p>
  return (
    <ul className="space-y-1 text-sm list-disc pl-5">
      {items
        .filter((i) => i[field])
        .map((item, idx) => (
          <li key={item.id || idx}>{item[field]}</li>
        ))}
    </ul>
  )
}

function KpiList({ items = [] }) {
  if (!items.length)
    return <p className="text-zinc-500 italic text-sm">None yet.</p>
  return (
    <ul className="space-y-1 text-sm">
      {items.map((item, idx) => (
        <li
          key={item.id || idx}
          className="flex justify-between border-b border-zinc-100 py-1 last:border-0"
        >
          <span>{item.name}</span>
          <span className="text-zinc-500">{item.target}</span>
        </li>
      ))}
    </ul>
  )
}
